import { useEffect, useState } from 'react'

/**
 * Wie lange ein Element nach dem Ausblenden noch steht.
 *
 * LÄNGER als die Abblende in `CanvasOptionsBar.scss` (150 ms bei
 * `canvas-options-bar-out` und `canvas-options-bar-flyout-out`), und das mit
 * Absicht: Der Zeitgeber startet im Effekt, die Animation erst im nächsten
 * Frame — bei gleicher Dauer wurde das Element ein, zwei Frames vor ihrem Ende
 * entfernt, und die Abblende endete als Schnitt. `forwards` in der SCSS hält
 * den Endzustand, die zusätzlichen Millisekunden sieht also niemand.
 */
export const PRESENCE_MS = 220

export interface Presence {
  /** Ob das Element gerendert wird — auch während es noch abblendet. */
  mounted: boolean
  /** Ob es gerade abblendet. Als `data-leaving` ans Element, die SCSS animiert daran. */
  leaving: boolean
}

/**
 * Ein Element sanft verschwinden lassen.
 *
 * React entfernt ein Element im selben Moment, in dem es nicht mehr gerendert
 * wird — für eine Abblende muss es noch kurz stehen. Dieser Hook hält es
 * `PRESENCE_MS` lang im Baum und meldet `leaving`, damit die SCSS rückwärts
 * spielen kann, was sie beim Erscheinen vorwärts spielt.
 *
 * Über einen Zeitgeber, nicht über `animationend`: Bei reduzierter Bewegung
 * oder in jsdom käme das Ereignis nie, und das Element bliebe für immer.
 *
 * @example
 * const bar = usePresence(showBar)
 * <NodeToolbar isVisible={bar.mounted}><CanvasOptionsBar leaving={bar.leaving} … /></NodeToolbar>
 */
export function usePresence(visible: boolean): Presence {
  /*
    Das Umschlagen wird WÄHREND des Renderns erkannt, nicht erst in einem
    Effekt: Erscheint das Element, muss es in DEMSELBEN Render im Baum sein
    (wer es gleich vermisst — etwa die Platzmessung eines Aufklappers — fände
    sonst nichts), und verschwindet es, muss es in demselben Render noch
    stehen, sonst wäre es weg, bevor irgendwer abblenden könnte.
  */
  const [seen, setSeen] = useState(visible)
  const [lingering, setLingering] = useState(false)
  if (seen !== visible) {
    setSeen(visible)
    setLingering(!visible)
  }

  useEffect(() => {
    if (visible) return
    /*
      Der Zeitgeber zählt erst ab dem ersten GEZEICHNETEN Frame, nicht ab dem
      Effekt. Nachgemessen: Nach einem Klick neben eine Mehrfachauswahl stand
      der Main Thread fast 300 ms still — die Abblende bekam keinen Frame,
      und beim ersten war der Zeitgeber längst abgelaufen. Das Element
      verschwand ohne jede Bewegung. Ein `requestAnimationFrame` feuert erst,
      wenn wieder gezeichnet wird; von da an laufen Animation und Zeitgeber
      gemeinsam.
    */
    let timer: ReturnType<typeof setTimeout> | undefined
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => setLingering(false), PRESENCE_MS)
    })
    return () => {
      cancelAnimationFrame(frame)
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [visible])

  return { mounted: visible || lingering, leaving: !visible && lingering }
}
